import React, { useCallback, useEffect, useState } from 'react'
import { FaPenNib, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import { Box, Button, Icon, Stack, Text, Badge, Select, ConfirmButton } from '@/components/lib'
import useStore from '@/store'
import api from '@/api'
import utils, { SIGNATURE_MEANINGS } from '@/utils/utils'
import useReauthentication from '@/hooks/useReauthentication'

// A participant's casebook — every response they have so far — and its
// signing status, with "Sign casebook" for an investigator: one deliberate
// act (one "confirm it's you") that signs every current response not
// already validly signed. The status rolls up from the responses, so a
// later amendment to one visit shows here as "partly signed" and only that
// visit needs signing again.

export function casebookBadge (casebook, { compact } = {}) {
  if (!casebook || casebook.status === 'none') return null
  const label = {
    signed: 'Signed',
    partly: `Partly signed ${casebook.signed}/${casebook.total}`,
    unsigned: 'Unsigned'
  }[casebook.status]
  const palette = { signed: 'green', partly: 'orange', unsigned: 'gray' }[casebook.status]
  return <Badge colorPalette={palette} variant='subtle' fontSize='xs'>{compact ? label : `Casebook ${label.toLowerCase()}`}</Badge>
}

export default function CasebookSignature ({ trialId, participantId, onSigned }) {
  const { user, trial } = useStore()
  const { withReauth } = useReauthentication()
  const [data, setData] = useState(null)
  const [signing, setSigning] = useState(false)
  const meanings = trial?.signatureMeanings?.length ? trial.signatureMeanings : SIGNATURE_MEANINGS
  const [meaning, setMeaning] = useState(meanings[0])

  const load = useCallback(() => {
    api.trials.getParticipantSignatures(trialId, participantId, setData, () => setData(null))
  }, [trialId, participantId])
  useEffect(() => { load() }, [load])

  if (!trial?.responseSigningEnabled || !data || data.casebook.status === 'none') return null
  const { casebook, signatures } = data
  const canSign = utils.canSignResponses(user, trial)
  const latest = signatures.find(s => s.status === 'valid') || signatures[0]
  const toSign = casebook.total - casebook.signed

  function sign () {
    setSigning(true)
    withReauth(`Signing ${toSign === 1 ? 'the 1 response' : `the ${toSign} responses`} in this casebook`, (token, resolve, reject) =>
      api.trials.signResponses(trial._id, { participant: participantId, meaning }, token, resolve, reject)
    ).then(result => {
      setSigning(false)
      load()
      if (onSigned) onSigned(result)
      utils.toast('success', 'Casebook signed', `${result.signatures?.length || 0} response${result.signatures?.length === 1 ? '' : 's'} signed as “${meaning}”.`)
    }).catch(err => {
      setSigning(false)
      if (!err?.cancelled) utils.toast('error', 'Unable to sign the casebook', err?.message)
    })
  }

  return (
    <Stack direction={['column', 'row']} alignItems={['stretch', 'center']} justifyContent='space-between' gap={3} p={3} rounded='md' borderWidth='1px' bg='bg.canvas'>
      <Stack direction='row' alignItems='center' gap={3} flexWrap='wrap'>
        <Icon as={casebook.status === 'signed' ? FaCheckCircle : casebook.status === 'partly' ? FaExclamationTriangle : FaPenNib} color={casebook.status === 'signed' ? 'green.500' : casebook.status === 'partly' ? 'orange.500' : 'fg.muted'} boxSize={5} />
        <Box>
          <Stack direction='row' alignItems='center' gap={2}>
            <Text fontWeight='bold'>Casebook</Text>
            {casebookBadge(casebook, { compact: true })}
          </Stack>
          <Text fontSize='sm' color='fg.muted'>
            {casebook.status === 'signed' && `All ${casebook.total} response${casebook.total === 1 ? '' : 's'} carry a valid signature.`}
            {casebook.status === 'partly' && `${toSign} of ${casebook.total} response${casebook.total === 1 ? '' : 's'} ${toSign === 1 ? 'needs' : 'need'} signing.`}
            {casebook.status === 'unsigned' && `${casebook.total} response${casebook.total === 1 ? '' : 's'}, none signed.`}
            {latest && ` Last signed by ${latest.signedByName} on ${utils.formatDateTime(latest.signedAt)} UTC.`}
          </Text>
        </Box>
      </Stack>
      {canSign && casebook.status !== 'signed' &&
        <Stack direction='row' alignItems='center' gap={2}>
          <Select size='sm' value={meaning} onChange={e => setMeaning(e.target.value)} variant='subtle' maxW='340px'>
            {meanings.map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
          <ConfirmButton
            header='Sign this casebook?'
            body={<Text>{toSign === 1 ? 'The 1 response' : `All ${toSign} responses`} not already signed will be signed as <em>“{meaning}”</em>, and you will be asked to confirm it&apos;s you.</Text>}
            actionColor='primary'
            trigger={
              <Button size='sm' colorPalette='primary' loading={signing}>
                <Icon as={FaPenNib} mr={1} /> Sign casebook
              </Button>
            }
            onConfirm={sign}
          />
        </Stack>}
    </Stack>
  )
}
